import { memo } from 'react';
import { View } from '@shared/components/View';
import { Field } from '@shared/data/entity/fields/Field';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import { AddressCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/AddressCollectionViewField';
import { CombinedTagsCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/CombinedTagsCollectionViewField';
import { DateCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/DateCollectionViewField';
import { DateRangeCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/DateRangeCollectionViewField';
import { EmailCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/EmailCollectionViewField';
import { EntityBatchCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/EntityBatchCollectionViewField';
import { FileCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/FileCollectionViewField';
import { GeolocationCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/GeolocationCollectionViewField';
import { NumberCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/NumberCollectionViewField';
import { PersonCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/PersonCollectionViewField';
import { RelatedCardCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/RelatedCardCollectionViewField';
import { RelatedLookupCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/RelatedLookupCollectionViewField';
import { ScheduledAutomationCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/ScheduledAutomationCollectionViewField';
import { SignatureCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/SignatureCollectionViewField';
import { SubformCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/SubformCollectionViewField';
import { TagCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/TagCollectionViewField';
import { TextCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/TextCollectionViewField';
import { TimeTrackerCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/TimeTrackerCollectionViewField';
import { TodoCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/TodoCollectionViewField';
import { UrlCollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/UrlCollectionViewField';
import { InputButton } from '@shared/scenes/cards/collectionView/components/staticComponents/InputButton';
import { isInputButtonComponent, isTimeTrackerComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import styles from './CardTableView.module.css';

interface CellProps {
  field?: Field | null;
  width: number;
  component: API.AnyComponent;
  workflowTemplateId: string;
  entityId: string;
  canUpdateCard: boolean;
  analytics: CollectionViewAnalytics;
  // tree view's pinned cell renders flush against the indent spacers: no
  // vertical or left padding, so the spacers can span the full row height
  isTreePinnedCell?: boolean;
}

function areCellPropsEqual(prev: CellProps, next: CellProps): boolean {
  return (
    prev.field === next.field &&
    prev.width === next.width &&
    prev.component === next.component &&
    prev.workflowTemplateId === next.workflowTemplateId &&
    prev.entityId === next.entityId &&
    prev.canUpdateCard === next.canUpdateCard &&
    prev.analytics === next.analytics &&
    prev.isTreePinnedCell === next.isTreePinnedCell
  );
}

function CellContent({
  field,
  width,
  component,
  workflowTemplateId,
  entityId,
  canUpdateCard,
  analytics,
  isTreePinnedCell,
}: CellProps) {
  return (
    <View
      className={isTreePinnedCell ? styles.treePinnedCell : styles.cell}
      style={isTreePinnedCell ? undefined : { width }}
    >
      <CellField
        field={field}
        component={component}
        workflowTemplateId={workflowTemplateId}
        entityId={entityId}
        canUpdateCard={canUpdateCard}
        analytics={analytics}
      />
    </View>
  );
}

export function CellField({
  field,
  component,
  workflowTemplateId,
  entityId,
  canUpdateCard,
  analytics,
}: Omit<CellProps, 'width' | 'isTreePinnedCell'>) {
  if (isInputButtonComponent(component)) {
    return (
      <InputButton
        component={component}
        workflowTemplateId={workflowTemplateId}
        entityId={entityId}
        analytics={analytics}
        canUpdateCard={canUpdateCard}
      />
    );
  }

  switch (field?.fieldType) {
    case 'AddressField':
      return <AddressCollectionViewField field={field} />;
    case 'CombinedTagsField':
      return <CombinedTagsCollectionViewField field={field} />;
    case 'DateField':
      return <DateCollectionViewField field={field} />;
    case 'DateRangeField':
      return <DateRangeCollectionViewField field={field} />;
    case 'EmailField':
      return <EmailCollectionViewField field={field} />;
    case 'EntityBatch':
      return <EntityBatchCollectionViewField field={field} />;
    case 'FileField':
      return <FileCollectionViewField field={field} />;
    case 'GeolocationField':
      return <GeolocationCollectionViewField field={field} />;
    case 'NumberField':
      return <NumberCollectionViewField field={field} />;
    case 'PersonField':
      return <PersonCollectionViewField field={field} />;
    case 'RelatedEntityField':
      return <RelatedCardCollectionViewField field={field} component={component} />;
    case 'RelatedLookupField':
      return (
        <RelatedLookupCollectionViewField
          field={field}
          component={component}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'ScheduledAutomationField':
      return <ScheduledAutomationCollectionViewField field={field} />;
    case 'SignatureField':
      return <SignatureCollectionViewField field={field} />;
    case 'SubformField':
      return <SubformCollectionViewField field={field} />;
    case 'TagField':
      return <TagCollectionViewField field={field} />;
    case 'TextField':
      return <TextCollectionViewField field={field} />;
    case 'TimeTrackerField':
      return (
        <>
          {isTimeTrackerComponent(component) && (
            <TimeTrackerCollectionViewField
              component={component}
              entityId={entityId}
              field={field}
              showIcon
              canUpdateCard={canUpdateCard}
            />
          )}
        </>
      );
    case 'TodoField':
      return <TodoCollectionViewField field={field} />;
    case 'UrlField':
      return <UrlCollectionViewField field={field} />;
    default:
      return null;
  }
}

export const Cell = memo(CellContent, areCellPropsEqual);
